/**
 * Feature Flags — co-located API client (P5 Wave 4 §5, READ-ONLY).
 *
 * Talks to quill-admin-backend `GET /api/admin/quill/flags`, which reads the
 * FEATURE_* env off each Quill service's RUNNING ECS task definition. There is
 * NO flag table and NO write path — flipping a flag is a deploy
 * (`set_feature_flag.sh`), never a UI toggle.
 *
 * The shared `request`/`unwrap` helpers in services/quillAdminService.ts are
 * module-private (not exported), so — per the contract — the tiny fetch/JSON +
 * envelope helper is replicated here verbatim. The reachable `QuillApiError`
 * class IS exported from the service and is reused so error handling (status +
 * `code`) stays identical across every Quill surface.
 *
 * ECS read failures surface as a 503 with `code: 'ECS_READ_FAILED'`, which
 * `request` parses onto `QuillApiError.code` so the page can show a clean
 * error + Retry rather than a raw stack.
 */

import { QuillApiError } from '../../../services/quillAdminService';

export { QuillApiError };

const BASE = '/api/admin/quill';

interface EnvelopeShape {
  success?: boolean;
  code?: string;
  message?: string;
  error?: string;
  [key: string]: unknown;
}

/**
 * Fetch + JSON. Throws QuillApiError on !ok or `{success:false}` (some routes
 * reply success:false on a 200), parsing `code` (e.g. ECS_READ_FAILED) off the
 * body when present. Mirrors services/quillAdminService.ts `request`.
 */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init.headers || {}),
      },
    });
  } catch (e) {
    throw new QuillApiError(
      e instanceof Error ? e.message : 'Network error contacting Quill backend',
      0,
      'NETWORK_ERROR'
    );
  }

  let body: EnvelopeShape | null = null;
  try {
    body = await res.json();
  } catch {
    // No/invalid JSON body — handled below.
  }

  if (!res.ok || (body && body.success === false)) {
    const message = body?.message || body?.error || `Quill backend request failed (${res.status})`;
    throw new QuillApiError(message, res.status, body?.code);
  }

  return (body ?? {}) as T;
}

// ── Types (frozen shape from contract §5) ──────────────────────────────────

/**
 * A single FEATURE_* flag.
 *
 * F15 (2026-08-03 audit): the page used to list only what was SET on the
 * running task definition, so the six engine flags with no env entry were
 * invisible — an operator asking "is FEATURE_STRICT_GROUNDING on?" got no
 * answer at all, not even "no". The backend now joins the live task-definition
 * read with a manifest of the flag names the code actually branches on.
 */
export interface FeatureFlag {
  name: string;
  /**
   * Raw env string, e.g. "true" / "false" / "" — never a boolean. `null` when
   * the flag is KNOWN but not set on this task definition: the service falls
   * back to its own code default, and we do not guess what that is.
   */
  value: string | null;
  /** 'task_definition' = set and live. 'unset' = known to the code, no env entry. */
  source?: 'task_definition' | 'unset';
  /** Whether the backend's manifest recognises this flag name. */
  known?: boolean;
  /** One line on what the flag turns on. Null for a set flag the manifest doesn't know. */
  effect?: string | null;
  /** Set but absent from the manifest — ECS is the truth, so it is still shown. */
  unknown_to_manifest?: boolean;
}

/** One Quill service and its FEATURE_* flags. */
export interface FeatureFlagServiceGroup {
  service: string;
  flags: FeatureFlag[];
  /**
   * False when the service's task definition could NOT be read (not deployed /
   * renamed). Its known flags are then shown as nothing at all rather than as
   * "unset" — that would be an inference from a failed read.
   */
  task_definition_read?: boolean;
}

/** `GET /flags` — flat body `{ success, services, read_at }`. */
export interface FeatureFlagsResponse {
  success?: boolean;
  services: FeatureFlagServiceGroup[];
  read_at: string;
}

export const flagsApi = {
  /** GET /flags — grouped FEATURE_* flags per Quill service. 503 → ECS_READ_FAILED. */
  getFlags: () => request<FeatureFlagsResponse>('/flags'),
};

export default flagsApi;
